/**
 * useAdminCursorList — generic hook per cursor-paginated lists nell'area admin.
 *
 * Consolida il pattern duplicato in AdminCustomersPage (l'unica oggi con
 * cursor end-to-end) e lo rende riusabile su tutte le altre tabelle admin
 * (Applications, Postings, Subscriptions, Users, AdminActions, EmailAudit).
 *
 * Features:
 *  - Fetch iniziale + auto-refetch quando deps cambiano (reset cursor)
 *  - loadMore() async con merge degli items
 *  - refresh() che resetta al primo page (per "Aggiorna" button)
 *  - totalCount esposto se BE lo restituisce (Firestore count() aggregation)
 *  - mutate(updater) per modifiche locali post-mutation (es. delete row
 *    senza fare refresh completo, ottimistic update)
 *
 * Backend contract: fetchFn deve restituire response shape
 *   { items: T[]; nextCursor: string | null; totalCount?: number }
 *
 * Esempio uso:
 *
 *   const filterParams = useMemo(() => ({ status, plan, ...}), [status, plan]);
 *   const { items, totalCount, loading, isRefreshing, error, refresh,
 *           loadingMore, hasMore, loadMore, mutate } = useAdminCursorList(
 *     (cursor) => adminApi.subscriptions.list({ ...filterParams, cursor }),
 *     [filterParams],
 *   );
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CursorListResponse<T> {
  items: T[];
  nextCursor: string | null;
  totalCount?: number;
  /** Some BE include hasMore as redundant signal (true if nextCursor !== null). */
  hasMore?: boolean;
}

export interface UseAdminCursorListResult<T> {
  /** Items caricati cumulativamente (page 1 + load more). */
  items: T[];
  /** Total filtered count from BE (Firestore count() aggregation). Undefined se BE non lo restituisce. */
  totalCount?: number;
  /** First-load spinner. */
  loading: boolean;
  /** Refresh in corso (re-fetch da pagina 1 mantenendo items vecchi visibili). */
  isRefreshing: boolean;
  /** Error message dalla request. */
  error: string | null;
  /** True se c'è una pagina successiva. */
  hasMore: boolean;
  /** Load more page spinner. */
  loadingMore: boolean;
  /** Manuale reload (reset pagina 1). */
  refresh: () => void;
  /** Carica next page e appendi. No-op se !hasMore o se già loading. */
  loadMore: () => Promise<void>;
  /** Mutate items locally (per ottimismo: es. delete row prima del refresh). */
  mutate: (updater: (prev: T[]) => T[]) => void;
}

export function useAdminCursorList<T>(
  fetchFn: (cursor: string | null) => Promise<CursorListResponse<T>>,
  deps: ReadonlyArray<unknown>,
): UseAdminCursorListResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Capture latest fetchFn senza forzare re-fetch quando l'identità cambia
  // (la fetchFn closure cattura filterParams; deps si occupano del re-fetch).
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // Fetch iniziale + re-fetch su deps change OR refresh tick.
  useEffect(() => {
    let cancelled = false;
    const isFirstLoad = items.length === 0;
    if (isFirstLoad) setLoading(true);
    else setIsRefreshing(true);

    fetchFnRef.current(null)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setNextCursor(res.nextCursor);
        setTotalCount(res.totalCount);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Fetch failed');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setIsRefreshing(false);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshTick]);

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchFnRef.current(nextCursor);
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
      // totalCount può cambiare se il filtro overlap con writes concorrenti
      if (res.totalCount !== undefined) setTotalCount(res.totalCount);
    } catch (err) {
      setError((err as Error).message || 'Load more failed');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  const mutate = useCallback((updater: (prev: T[]) => T[]) => {
    setItems((prev) => updater(prev));
    // totalCount viene aggiornato al prossimo refresh; per ora lo decrementiamo
    // ottimisticamente di 1 se l'updater ha rimosso elementi.
    setTotalCount((prev) => {
      if (prev === undefined) return prev;
      // Approssimazione: ricalcoliamo come prev - (items length diff)
      // Per ora lasciamo a refresh il totalCount, l'utente vede la lista corretta.
      return prev;
    });
  }, []);

  return {
    items,
    totalCount,
    loading,
    isRefreshing,
    error,
    hasMore: nextCursor !== null,
    loadingMore,
    refresh,
    loadMore,
    mutate,
  };
}
